<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Icon from './Icon.vue'

const props = defineProps({
  ariaLabel: {
    type: String,
    required: true,
  },
  modelValue: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  variant: {
    type: String,
    default: 'desktop',
    validator: (value) => ['desktop', 'mobile'].includes(value),
  },
})

const emit = defineEmits(['update:modelValue'])

const rootRef = ref(null)
const sliderStyle = ref(getInitialSliderStyle())
const buttonElements = new Map()

const isMobileVariant = computed(() => props.variant === 'mobile')
const optionsKey = computed(() => props.options.map((option) => `${option.value}:${option.label}`).join('|'))

const rootClasses = computed(() => (isMobileVariant.value
  ? 'relative flex flex-row rounded-full border border-[#2F3033] bg-white shadow-sm p-0.5 transition-all duration-300 w-full items-center isolate'
  : 'relative flex h-12 items-center tripfinder-toggle rounded-full border border-[#2F3033] bg-white shadow-sm transition-all duration-300 w-auto flex-shrink-0 isolate'))

const sliderClasses = computed(() => (isMobileVariant.value
  ? 'absolute tripfinder-toggle-slider top-0 bottom-0 rounded-full bg-[#2F3033] border border-gray-900/10 shadow-sm transition-all duration-300 z-10'
  : 'absolute tripfinder-toggle-slider -top-[1px] -bottom-[1px] rounded-full bg-[#2F3033] border border-gray-900/10 transition-all duration-300 z-0 pointer-events-none'))

const buttonClasses = computed(() => (isMobileVariant.value
  ? 'relative z-20 flex-1 flex items-center justify-center text-lg font-semibold rounded-full transition-all duration-200 focus:outline-none active:shadow-md active:scale-95 whitespace-nowrap leading-tight text-center'
  : 'relative z-20 py-2 px-4 text-base font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center flex-shrink-0'))

function getInitialSliderStyle() {
  return props.variant === 'mobile'
    ? { left: '2px', width: '0px' }
    : { left: '4px', width: '0px' }
}

function setButtonRef(value) {
  return (element) => {
    if (element) {
      buttonElements.set(value, element)
      return
    }

    buttonElements.delete(value)
  }
}

function selectOption(value) {
  emit('update:modelValue', value)
}

function updateSlider() {
  if (!rootRef.value) return

  const activeOption = props.options.find((option) => option.value === props.modelValue)
  const activeButton = activeOption ? buttonElements.get(activeOption.value) : null

  if (!activeOption || !activeButton) return

  if (isMobileVariant.value) {
    const width = Math.ceil(activeButton.offsetWidth)
    let left = Math.max(0, Math.floor(activeButton.offsetLeft)) - 1

    left += activeOption.sliderLeftOffset ?? 0

    sliderStyle.value = {
      left: `${left}px`,
      width: `${width + (activeOption.sliderWidthOffset ?? 0)}px`,
    }

    return
  }

  sliderStyle.value = {
    left: `${activeButton.offsetLeft}px`,
    width: `${activeButton.offsetWidth}px`,
  }
}

async function syncSlider() {
  await nextTick()
  updateSlider()
}

watch(() => props.modelValue, () => {
  void syncSlider()
})

watch(optionsKey, () => {
  void syncSlider()
})

onMounted(() => {
  window.addEventListener('resize', updateSlider)
  void syncSlider()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSlider)
})
</script>

<template>
  <div
    ref="rootRef"
    :class="rootClasses"
    role="tablist"
    :aria-label="ariaLabel"
  >
    <div
      class="tripfinder-toggle-slider"
      :class="sliderClasses"
      :style="sliderStyle"
      aria-hidden="true"
    ></div>

    <button
      v-for="option in options"
      :key="option.value"
      :ref="setButtonRef(option.value)"
      type="button"
      role="tab"
      :aria-selected="modelValue === option.value"
      :class="[
        buttonClasses,
        option.buttonClass,
        modelValue === option.value ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]',
      ]"
      @click="selectOption(option.value)"
      @keyup.enter="selectOption(option.value)"
    >
      <div
        v-if="option.iconName"
        :class="option.contentClass ?? 'flex items-center justify-center'"
      >
        <Icon
          :name="option.iconName"
          :class="option.iconClass ?? 'w-4 h-4 mr-2'"
        />
        <span>{{ option.label }}</span>
      </div>
      <span v-else>{{ option.label }}</span>
    </button>
  </div>
</template>

<style scoped>
.tripfinder-toggle {
  isolation: isolate;
}

.tripfinder-toggle-slider {
  z-index: 0 !important;
}
</style>
